import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import { PageGuard } from "@/components/shared/PlanGuard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Zap, Plus, ClipboardList, AlertTriangle, CheckCircle2,
  Calendar, ChevronRight, RefreshCw, FileText, Upload, File, Trash2, Download, Eye, FolderOpen,
} from "lucide-react";
import InstallationForm from "@/components/electrical/InstallationForm";
import InspectionForm from "@/components/electrical/InspectionForm";
import DeficiencyList from "@/components/electrical/DeficiencyList";
import {
  InstallationStatusBadge, InspectionResultBadge,
} from "@/components/electrical/ElectricalStatusBadge";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DOC_CATEGORIES = [
  { code: "technical_report", label: "Informes tecnics", icon: FileText, color: "bg-blue-100 text-blue-700" },
  { code: "installation_cert", label: "Certificat d'installacio", icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700" },
  { code: "electrical_schema", label: "Esquema electric", icon: Zap, color: "bg-amber-100 text-amber-700" },
  { code: "other", label: "Altres documents", icon: FolderOpen, color: "bg-gray-100 text-gray-700" },
];

function DaysUntil({ dateStr }) {
  if (!dateStr) return <span className="text-xs text-gray-400">-</span>;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return <span className="text-xs font-semibold text-red-600">Caducada ({Math.abs(diff)}d)</span>;
  if (diff <= 30) return <span className="text-xs font-semibold text-orange-500">{diff}d</span>;
  if (diff <= 90) return <span className="text-xs font-semibold text-yellow-600">{diff}d</span>;
  return <span className="text-xs text-green-600">{diff}d</span>;
}

// ─── Electrical Documents ─────────────────────────────────────────────────────
function ElectricalDocuments({ venueId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);

  useEffect(() => {
    if (venueId) loadDocs();
  }, [venueId]);

  const loadDocs = async () => {
    setLoading(true);
    const allDocs = await hosys.entities.ElectricalDocument.filter({ venue_id: venueId });
    setDocs(allDocs.sort((a, b) => (b.created_date || "").localeCompare(a.created_date || "")));
    setLoading(false);
  };

  const handleUpload = async (category) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.jpg,.jpeg,.png,.doc,.docx";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(category);
      try {
        const formData = new FormData();
        formData.append("file", file);
        const uploadRes = await fetch(`${API_URL}/api/upload`, {
          method: "POST",
          credentials: 'include',
          body: formData,
        });
        const uploadData = await uploadRes.json();
        if (!uploadRes.ok) throw new Error(uploadData.detail || "Error uploading");

        await hosys.entities.ElectricalDocument.create({
          venue_id: venueId,
          category,
          name: file.name,
          file_url: uploadData.url,
          file_type: file.type,
          file_size: file.size,
        });
        loadDocs();
      } catch (err) {
        console.error("Upload error:", err);
        console.warn("Error al pujar el fitxer: " + err.message);
      }
      setUploading(null);
    };
    input.click();
  };

  const handleDelete = async (doc) => {
    await hosys.entities.ElectricalDocument.delete(doc.id);
    loadDocs();
  };

  if (loading) return <div className="py-8 text-center text-gray-400 text-sm">Carregant documents...</div>;

  return (
    <div className="space-y-6" data-testid="electrical-documents">
      {DOC_CATEGORIES.map(cat => {
        const catDocs = docs.filter(d => d.category === cat.code);
        const Icon = cat.icon;
        return (
          <div key={cat.code} className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cat.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-800">{cat.label}</h3>
                  <p className="text-[10px] text-gray-400">{catDocs.length} document{catDocs.length !== 1 ? "s" : ""}</p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleUpload(cat.code)}
                disabled={uploading === cat.code}
                className="gap-1.5 text-xs"
                data-testid={`upload-${cat.code}`}
              >
                {uploading === cat.code ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Upload className="w-3.5 h-3.5" />
                )}
                Pujar fitxer
              </Button>
            </div>
            {catDocs.length === 0 ? (
              <div className="border border-dashed border-gray-200 rounded-xl p-6 text-center">
                <File className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                <p className="text-xs text-gray-400">Cap document pujat</p>
                <button onClick={() => handleUpload(cat.code)} className="text-xs text-[#7B2D3F] font-medium mt-1 hover:underline">
                  Pujar primer document
                </button>
              </div>
            ) : (
              <div className="space-y-1.5">
                {catDocs.map(doc => (
                  <div key={doc.id} className="flex items-center gap-3 p-3 bg-white border border-gray-100 rounded-xl hover:shadow-sm transition-shadow">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${cat.color}`}>
                      <File className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{doc.name}</p>
                      <p className="text-[10px] text-gray-400">
                        {doc.created_date ? new Date(doc.created_date).toLocaleDateString("ca-ES") : ""}
                        {doc.file_size ? ` · ${(doc.file_size / 1024).toFixed(0)} KB` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {doc.file_url && (
                        <>
                          <a href={doc.file_url} target="_blank" rel="noopener noreferrer"
                            className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" title="Veure">
                            <Eye className="w-3.5 h-3.5" />
                          </a>
                          <a href={doc.file_url} download
                            className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors" title="Descarregar">
                            <Download className="w-3.5 h-3.5" />
                          </a>
                        </>
                      )}
                      <button onClick={() => handleDelete(doc)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="Eliminar">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StatsBar({ installations, inspections, deficiencies }) {
  const expired = installations.filter(i => i.status === "expired" || i.status === "deficiency_major").length;
  const pending = installations.filter(i => i.status === "pending_inspection").length;
  const openDefic = deficiencies.filter(d => d.status === "open" || d.status === "in_progress").length;
  const ok = installations.filter(i => i.status === "ok").length;

  const stats = [
    { label: "Conformes", value: ok, color: "text-green-600", bg: "bg-green-50" },
    { label: "Pendent inspecció", value: pending, color: "text-yellow-600", bg: "bg-yellow-50" },
    { label: "Deficiències obertes", value: openDefic, color: "text-orange-600", bg: "bg-orange-50" },
    { label: "Caducades / Greus", value: expired, color: "text-red-600", bg: "bg-red-50" },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
      {stats.map(s => (
        <div key={s.label} className={`${s.bg} rounded-2xl p-4 flex flex-col gap-1`}>
          <span className={`text-2xl font-bold ${s.color}`}>{s.value}</span>
          <span className="text-xs text-gray-500 font-medium">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

function InstallationCard({ installation, deficiencies, onEdit, onAddInspection }) {
  const myDefic = deficiencies.filter(d => d.installation_id === installation.id);
  const openDefic = myDefic.filter(d => d.status === "open" || d.status === "in_progress");

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-semibold text-gray-900 text-sm">{installation.name}</h3>
            <InstallationStatusBadge status={installation.status} />
          </div>
          <p className="text-xs text-gray-500">
            {installation.installation_type?.replace(/_/g, " ")}
            {installation.location && ` · ${installation.location}`}
            {installation.power_kw && ` · ${installation.power_kw} kW`}
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button size="sm" variant="outline" onClick={() => onEdit(installation)} className="text-xs h-8">Editar</Button>
          <Button size="sm" onClick={() => onAddInspection(installation)} className="text-xs h-8 bg-[#7B2D3F] hover:bg-[#6a2636] text-white gap-1">
            <Plus className="w-3 h-3" /> Inspecció
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <p className="text-gray-400 mb-0.5">Última inspecció</p>
          <p className="text-gray-700 font-medium">
            {installation.last_inspection_date
              ? new Date(installation.last_inspection_date).toLocaleDateString("ca-ES")
              : "—"}
          </p>
        </div>
        <div>
          <p className="text-gray-400 mb-0.5">Pròxima inspecció</p>
          <div className="flex items-center gap-1">
            <p className="text-gray-700 font-medium">
              {installation.next_inspection_date
                ? new Date(installation.next_inspection_date).toLocaleDateString("ca-ES")
                : "—"}
            </p>
            <DaysUntil dateStr={installation.next_inspection_date} />
          </div>
        </div>
        {installation.rebt_itc && (
          <div>
            <p className="text-gray-400 mb-0.5">ITC-BT</p>
            <p className="text-gray-700 font-medium">{installation.rebt_itc}</p>
          </div>
        )}
        {installation.installer_company && (
          <div>
            <p className="text-gray-400 mb-0.5">Instal·lador</p>
            <p className="text-gray-700 font-medium truncate">{installation.installer_company}</p>
          </div>
        )}
      </div>

      {openDefic.length > 0 && (
        <div className="mt-3 flex items-center gap-2 text-xs bg-orange-50 text-orange-700 rounded-lg px-3 py-2">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          {openDefic.length} deficiència{openDefic.length > 1 ? "s" : ""} pendent{openDefic.length > 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
}

export default function ElectricalCompliance() {
  const { currentVenue } = useVenue();
  const [installations, setInstallations] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [deficiencies, setDeficiencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInstallForm, setShowInstallForm] = useState(false);
  const [editingInstall, setEditingInstall] = useState(null);
  const [showInspectForm, setShowInspectForm] = useState(false);
  const [selectedInstall, setSelectedInstall] = useState(null);

  const load = async () => {
    if (!currentVenue) return;
    setLoading(true);
    const [inst, insp, defic] = await Promise.all([
      hosys.entities.ElectricalInstallation.filter({ venue_id: currentVenue.id, active: true }),
      hosys.entities.ElectricalInspection.filter({ venue_id: currentVenue.id }, "-inspection_date", 50),
      hosys.entities.ElectricalDeficiency.filter({ venue_id: currentVenue.id }),
    ]);
    setInstallations(inst);
    setInspections(insp);
    setDeficiencies(defic);
    setLoading(false);
  };

  useEffect(() => { load(); }, [currentVenue?.id]);

  const handleAddInspection = (install) => {
    setSelectedInstall(install);
    setShowInspectForm(true);
  };

  const handleEditInstall = (install) => {
    setEditingInstall(install);
    setShowInstallForm(true);
  };

  const openDeficiencies = deficiencies.filter(d => d.status === "open" || d.status === "in_progress");
  const upcomingInspections = installations
    .filter(i => i.next_inspection_date)
    .sort((a, b) => new Date(a.next_inspection_date) - new Date(b.next_inspection_date))
    .slice(0, 5);

  return (
    <PageGuard moduleCode="electrical_compliance">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
              <Zap className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Instal·lació Elèctrica</h1>
              <p className="text-xs text-gray-500">Compliment REBT · ITC-BT-28</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} className="gap-1">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
            <Button onClick={() => { setEditingInstall(null); setShowInstallForm(true); }}
              className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white gap-1.5" size="sm">
              <Plus className="w-4 h-4" /> Nova instal·lació
            </Button>
          </div>
        </div>

        <StatsBar installations={installations} inspections={inspections} deficiencies={deficiencies} />

        <Tabs defaultValue="installations">
          <TabsList className="mb-4">
            <TabsTrigger value="installations" className="gap-1.5">
              <Zap className="w-4 h-4" /> Instal·lacions ({installations.length})
            </TabsTrigger>
            <TabsTrigger value="inspections" className="gap-1.5">
              <ClipboardList className="w-4 h-4" /> Inspeccions ({inspections.length})
            </TabsTrigger>
            <TabsTrigger value="deficiencies" className="gap-1.5">
              <AlertTriangle className="w-4 h-4" />
              Deficiències
              {openDeficiencies.length > 0 && (
                <Badge className="bg-red-100 text-red-700 ml-1">{openDeficiencies.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="calendar" className="gap-1.5">
              <Calendar className="w-4 h-4" /> Proximes
            </TabsTrigger>
            <TabsTrigger value="documents" className="gap-1.5">
              <FolderOpen className="w-4 h-4" /> Documents
            </TabsTrigger>
          </TabsList>

          {/* INSTAL·LACIONS */}
          <TabsContent value="installations">
            {loading ? (
              <div className="text-center py-12 text-gray-400">Carregant...</div>
            ) : installations.length === 0 ? (
              <div className="text-center py-16">
                <Zap className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">Cap instal·lació registrada</p>
                <p className="text-sm text-gray-400 mb-4">Afegeix les instal·lacions elèctriques del local</p>
                <Button onClick={() => setShowInstallForm(true)} className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white">
                  <Plus className="w-4 h-4 mr-2" /> Afegir primera instal·lació
                </Button>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {installations.map(inst => (
                  <InstallationCard
                    key={inst.id}
                    installation={inst}
                    deficiencies={deficiencies}
                    onEdit={handleEditInstall}
                    onAddInspection={handleAddInspection}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* INSPECCIONS */}
          <TabsContent value="inspections">
            {inspections.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <FileText className="w-10 h-10 mx-auto mb-2 text-gray-200" />
                <p>No hi ha inspeccions registrades</p>
              </div>
            ) : (
              <div className="space-y-2">
                {inspections.map(insp => (
                  <div key={insp.id} className="bg-white border border-gray-100 rounded-xl p-4 flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-sm font-medium text-gray-900">{insp.installation_name}</span>
                        <InspectionResultBadge result={insp.result} />
                      </div>
                      <p className="text-xs text-gray-500">
                        {new Date(insp.inspection_date).toLocaleDateString("ca-ES")}
                        {insp.inspector_entity && ` · ${insp.inspector_entity}`}
                        {insp.inspector_name && ` · ${insp.inspector_name}`}
                      </p>
                      {(insp.deficiencies || []).length > 0 && (
                        <p className="text-xs text-orange-600 mt-1">
                          {insp.deficiencies.length} deficiència{insp.deficiencies.length > 1 ? "s" : ""} detectada{insp.deficiencies.length > 1 ? "es" : ""}
                        </p>
                      )}
                    </div>
                    {insp.next_inspection_date && (
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-gray-400">Pròxima</p>
                        <p className="text-xs font-medium text-gray-700">{new Date(insp.next_inspection_date).toLocaleDateString("ca-ES")}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* DEFICIÈNCIES */}
          <TabsContent value="deficiencies">
            <DeficiencyList deficiencies={deficiencies} onRefresh={load} />
          </TabsContent>

          {/* PRÒXIMES INSPECCIONS */}
          <TabsContent value="calendar">
            <div className="space-y-3">
              <p className="text-sm text-gray-500 mb-4">Instal·lacions ordenades per data de pròxima inspecció</p>
              {upcomingInspections.length === 0 ? (
                <div className="text-center py-10 text-gray-400">Cap inspecció programada</div>
              ) : (
                upcomingInspections.map(inst => {
                  const diff = Math.ceil((new Date(inst.next_inspection_date) - new Date()) / (1000 * 60 * 60 * 24));
                  const urgent = diff <= 30;
                  const warning = diff <= 90 && diff > 30;
                  return (
                    <div key={inst.id} className={`flex items-center gap-4 p-4 rounded-xl border ${urgent ? "border-red-200 bg-red-50" : warning ? "border-yellow-200 bg-yellow-50" : "border-gray-100 bg-white"}`}>
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${urgent ? "bg-red-100" : warning ? "bg-yellow-100" : "bg-green-100"}`}>
                        <Calendar className={`w-5 h-5 ${urgent ? "text-red-600" : warning ? "text-yellow-600" : "text-green-600"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900">{inst.name}</p>
                        <p className="text-xs text-gray-500">{inst.location || inst.installation_type?.replace(/_/g, " ")}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-gray-900">
                          {new Date(inst.next_inspection_date).toLocaleDateString("ca-ES")}
                        </p>
                        <DaysUntil dateStr={inst.next_inspection_date} />
                      </div>
                      <Button size="sm" onClick={() => handleAddInspection(inst)} className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white flex-shrink-0">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* DOCUMENTS */}
          <TabsContent value="documents">
            <ElectricalDocuments venueId={currentVenue?.id} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Forms */}
      {showInstallForm && (
        <InstallationForm
          installation={editingInstall}
          venueId={currentVenue?.id}
          onSave={() => { setShowInstallForm(false); setEditingInstall(null); load(); }}
          onClose={() => { setShowInstallForm(false); setEditingInstall(null); }}
        />
      )}
      {showInspectForm && selectedInstall && (
        <InspectionForm
          installation={selectedInstall}
          venueId={currentVenue?.id}
          onSave={() => { setShowInspectForm(false); setSelectedInstall(null); load(); }}
          onClose={() => { setShowInspectForm(false); setSelectedInstall(null); }}
        />
      )}
    </PageGuard>
  );
}